import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { Scissors, Droplets, TreePine, Flower2, Bug, Shovel, CheckCircle2, ArrowRight } from 'lucide-react'

const services = [
  {
    id: 1,
    title: 'Lawn Mowing',
    description: 'Regular mowing with professional equipment for a perfectly manicured lawn.',
    icon: Scissors,
    features: ['Weekly/Bi-weekly', 'Edge trimming', 'Clipping cleanup'],
    price: 'From $35/visit',
  },
  {
    id: 2,
    title: 'Fertilization',
    description: 'Custom fertilization programs to keep your lawn lush and healthy.',
    icon: Droplets,
    features: ['Soil testing', 'Seasonal formulas', 'Organic options'],
    price: 'From $45/application',
  },
  {
    id: 3,
    title: 'Tree & Shrub Care',
    description: 'Expert pruning, trimming, and health assessments.',
    icon: TreePine,
    features: ['Precision pruning', 'Disease treatment', 'Deep root feeding'],
    price: 'From $75/service',
  },
  {
    id: 4,
    title: 'Landscaping',
    description: 'Transform your outdoor space with custom design and installation.',
    icon: Flower2,
    features: ['Custom designs', 'Plant selection', 'Hardscaping'],
    price: 'Custom quotes',
  },
  {
    id: 5,
    title: 'Pest Control',
    description: 'Eco-friendly pest management to protect your lawn and family.',
    icon: Bug,
    features: ['Grub control', 'Mosquito treatment', 'Pet-safe products'],
    price: 'From $55/treatment',
  },
  {
    id: 6,
    title: 'Seasonal Cleanup',
    description: 'Spring and fall cleanup services for changing seasons.',
    icon: Shovel,
    features: ['Leaf removal', 'Debris cleanup', 'Aeration'],
    price: 'From $120/service',
  },
]

export default function Services() {
  const ref = useRef()
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [activeService, setActiveService] = useState(null)

  return (
    <section id="services" className="section py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#2d7d32]/10 text-[#2d7d32] rounded-full text-sm font-semibold mb-4">
            <Scissors className="w-4 h-4" />
            What We Offer
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="gradient-text">Services</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Comprehensive lawn care solutions tailored to your property's needs.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
          {services.map((service, i) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group cursor-pointer w-full max-w-sm"
              onClick={() => setActiveService(activeService === service.id ? null : service.id)}
            >
              <div className="h-full p-6 bg-[#f8faf7] rounded-2xl border border-gray-100 hover:border-[#2d7d32]/30 hover:shadow-lg transition-all duration-300 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#2d7d32]/10 flex items-center justify-center mb-4 mx-auto">
                  <service.icon className="w-6 h-6 text-[#2d7d32]" />
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#2d7d32] transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {service.description}
                </p>

                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm font-semibold text-[#2d7d32]">{service.price}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-[#2d7d32] transition-colors" />
                </div>

                <AnimatePresence>
                  {activeService === service.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 mt-4 border-t border-gray-200">
                        <ul className="space-y-2">
                          {service.features.map((feature) => (
                            <li key={feature} className="flex items-center justify-center gap-2 text-sm text-gray-600">
                              <CheckCircle2 className="w-4 h-4 text-[#4caf50]" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <a
                          href="#booking"
                          className="mt-4 block w-full text-center py-2.5 bg-[#2d7d32] text-white rounded-lg text-sm font-semibold hover:bg-[#1b5e20] transition-colors"
                        >
                          Book This Service
                        </a>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
